'use strict';

const realtime = require("realtime");

const
  Utilities = realtime.Utilities,
  RealtimeValue = realtime.RealtimeValue,
  RealtimeObject = realtime.RealtimeObject,
  Property = realtime.Property,
  Reference = realtime.Reference,
  Relation = realtime.Relation,
  File = realtime.File,
  Values = realtime.Values,
  AssociatedValues = realtime.AssociatedValues,
  Relations = realtime.Relations
;
const Representers = require("./representers");
const Coverage = require("./primitives").Coverage;

function statisticRef(from) {
  return from.root.child("statistic/" + Utilities.rootPath(from));
}
function notificationsRef(from) {
  return from.root.child("notifications/" + Utilities.rootPath(from));
}
function feedRef(from) {
  return from.root.child("feed/" + Utilities.rootPath(from));
}
function invitationsRef(from) {
  return from.root.child("invitations/" + Utilities.rootPath(from));
}

Reference.Team = function(ref, options, obsolete) {
  options.value = { class: Team, path: "teams", obsoleteMode: obsolete };
  return new Reference(ref, options);
};
Reference.User = function(ref, options, obsolete) {
  options.value = { class: User, path: "users", obsoleteMode: obsolete };
  return new Reference(ref, options);
};

RealtimeObject.prototype.notifications = function() {
  return new Values(notificationsRef(this.ref.child("sent")), {
    elementClass: Message
  });
};
RealtimeObject.prototype.subscribers = function() {
  return new Relations(feedRef(this.ref.child("subscribers")), {
    element: {
      path: "feed",
      class: Subscriber,
      property: k => `subscriptions/${k}`
    }
  });
};

// models

exports.Contacts = class Contacts {
  constructor() {
    this.phone = "";
  }
}

exports.Gender = Object.freeze({
  male: 0,
  female: 1
});

exports.AgeGroup = Object.freeze({
  childs: 0,
  juniors: 1,
  adults: 2
});

exports.TournamentType = Object.freeze({
  championship: 0,
  cup: 1,
  match: 2
});

class User extends RealtimeObject {
  constructor(ref, options = {}) {
    const props = options.props || {};
    super(ref, options);
    this.name = new Property(ref.child("name"), { val: props.name });
    this.gender = new Property(ref.child("gender"), { val: props.gender });
    this.photo = new File.png(ref.child("photo"), {
      val: props.photo,
      optional: true
    });
    this.thumbPhoto = new File.png(ref.child("thumb_photo"), {
      val: props.thumbPhoto,
      readonly: true,
      optional: true
    });
    this.location = new Property(ref.child("location"), {
      val: props.location,
      optional: true
    });
    this.address = new Property(ref.child("address"), { val: props.address });
    this.birthdate = new Property(ref.child("birthdate"), {
      val: props.birthdate
    });
    this.contacts = new Property(ref.child("contacts"), {
      val: props.contacts,
      optional: true
    });
    this.metrics = new Property(ref.child("metrics"), {
      val: props.metrics,
      optional: true
    });
    this.roles = new Property(ref.child("roles"), {
      val: props.roles,
      representer: Representers.defaultValue(
        Representers.valueRepresenter(true),
        User.Roles.anonym
      )
    });
    this.footballer = new FootballPlayer(ref.child("footballer"), {
      props: props.footballer
    });
    this.teamManager = new Team.Manager(ref.child("teamManager"), {
      props: props.teamManager
    });
    this.leagueManager = new League.Manager(ref.child("leagueManager"), {
      props: props.leagueManager
    });
  }

  conditionForWrite(prop, label) {
    switch (label) {
      case "footballer":
        return User.Roles.contains(User.Roles.footballer, this.roles.value);
      case "teamManager":
        return User.Roles.contains(User.Roles.teamManager, this.roles.value);
      case "leagueManager":
        return User.Roles.contains(User.Roles.leagueManager, this.roles.value);
      default:
        return super.conditionForWrite(prop, label);
    }
  }

  conditionForRead(prop, label, snapshot) {
    switch (label) {
      case "footballer":
        return User.Roles.contains(
          User.Roles.footballer,
          this.roles.apply(snapshot.child(this.roles.key))
        );
      case "teamManager":
        return User.Roles.contains(
          User.Roles.teamManager,
          this.roles.apply(snapshot.child(this.roles.key))
        );
      case "leagueManager":
        return User.Roles.contains(
          User.Roles.leagueManager,
          this.roles.apply(snapshot.child(this.roles.key))
        );
      default:
        return super.conditionForWrite(prop, label);
    }
  }
}
User.Roles = Object.freeze({
  anonym: 0,
  user: 1 << 0,
  footballer: 1 << 1,
  teamManager: 1 << 2,
  leagueManager: 1 << 3,
  all() {
    return [this.user, this.footballer, this.teamManager, this.leagueManager];
  },
  contains(role, inRoles) {
    return ((inRoles || 0) & role) != 0;
  }
});
User.prototype.leagueIfKeeps = function() {
  const user = this;
  return user.roles.loadIfUndefined().then(roles => {
    if ((roles & User.Roles.leagueManager) == 0) return null;

    return user.leagueManager.league.loadIfUndefined();
  });
};
User.prototype.teamIfKeeps = function() {
  const user = this;
  return user.roles.loadIfUndefined().then(roles => {
    if ((roles & User.Roles.teamManager) == 0) return null;

    return user.teamManager.team.loadIfUndefined();
  });
};
User.prototype.isFootballer = function() {
  return this.roles.loadIfUndefined().then(roles => {
    return (roles & User.Roles.footballer) != 0;
  });
};
User.prototype.isUser = function() {
  return this.roles.loadIfUndefined().then(roles => {
    return User.Roles.contains(User.Roles.user, roles);
  });
};
User.prototype.subscriptions = function() {
  return new Relations(feedRef(this.ref.child("subscriptions")), {
    element: {
      class: Subscription,
      path: "feed",
      property: k => `subscribers/${k}`
    }
  });
};
User.prototype.customInvitationNames = function() {
  return new AssociatedValues(invitationsRef(this.ref.child("names")), {
    keyObject: { class: Team, path: "teams" },
    valueClass: String
  });
};
User.prototype.currentNameOrName = function(team) {
  return this.roles
    .loadIfUndefined()
    .then(roles => {
      return (roles & User.Roles.user) != 0;
    })
    .then(contains => {
      if (contains) {
        return this.name.loadIfUndefined();
      } else if (team) {
        return this.customInvitationNames().valueFor(team);
      } else {
        return this.customInvitationNames()
          .load()
          .then(names => (names.length ? names[0][1] : null)); // TODO: Load only first
      }
    });
};
User.prototype.feed = function() {
  return feedRef(this.ref).child('feed');
};
User.prototype.receivedNotifications = function() {
  return notificationsRef(this.ref).child('received');
};
exports.User = User;
class FootballPlayer extends RealtimeObject {
  constructor(ref, options = {}) {
    const props = options.props || {};
    super(ref, options);
    this.shirtName = new Property(ref.child("shirtName"), {
      val: props.shirtName
    });
    this.condition = new Property(ref.child("condition"), {
      val: props.condition,
      optional: true
    });
    this.position = new Property(ref.child("position"), {
      val: props.position,
      representer: Representers.defaultValue(
        Representers.valueRepresenter(true),
        FootballPlayer.Position.universal
      )
    });
    this.preferredFoot = new Property(ref.child("preferredFoot"), {
      val: props.preferredFoot,
      optional: true
    });
    this.teams = new Relations(ref.child("teams"), {
      element: {
        class: Team,
        property: k => `members/${k}`,
        ownerLevelsUp: 2
      }
    });
  }
}
FootballPlayer.Position = Object.freeze({
  universal: 0,
  goalkeeper: 1,
  defender: 2,
  midfielder: 3,
  striker: 4,
  all() {
    return [
      this.universal,
      this.goalkeeper,
      this.defender,
      this.midfielder,
      this.striker
    ];
  }
});
FootballPlayer.PreferredFoot = Object.freeze({
  right: 0,
  left: 1
});
exports.FootballPlayer = FootballPlayer;

class Team extends RealtimeObject {
  constructor(ref, options = {}) {
    const props = options.props || {};
    super(ref, options);
    this.name = new Property(ref.child("name"), { val: props.name });
    this.shortName = new Property(ref.child("shortName"), {
      val: props.shortName
    });
    this.contacts = new Property(ref.child("contacts"), {
      val: props.contacts,
      optional: true
    });
    this.location = new Property(ref.child("location"), {
      val: props.location,
      optional: true
    });
    this.address = new Property(ref.child("address"), { val: props.address });
    this.manager = new Relation(ref.child("manager"), {
      val: props.manager,
      value: { class: User, property: "teamManager/team" }
    });
    this.logo = new File.png(ref.child("logo"), {
      val: props.logo,
      optional: true
    });
    this.thumbLogo = new File.png(ref.child("thumb_logo"), {
      val: props.thumbLogo,
      optional: true,
      readonly: true
    });
    this.members = new Relations(ref.child("members"), {
      element: {
        class: User,
        property: k => `footballer/teams/${k}`
      }
    });

    this.info = new Property(ref.child("info"), {
      val: props.info,
      optional: true,
      representer: Representers.teamInfoRepresenter
    });
    this.leagues = new Relations(ref.child("leagues"), {
      element: {
        class: League,
        property: k => `teams/${k}`
      }
    });
  }
}
Team.Manager = class extends RealtimeObject {
  constructor(ref, options = {}) {
    const props = options.props || {};
    super(ref, options);
    this.team = new Relation(ref.child("team"), {
      val: props.team,
      value: { class: Team, property: "manager", ownerLevelsUp: 2 }
    });
  }
};
exports.Team = Team;

class League extends RealtimeObject {
  constructor(ref, options = {}) {
    const props = options.props || {};
    super(ref, options);
    this.title = new Property(ref.child("title"), { val: props.title });
    this.shortTitle = new Property(ref.child("shortTitle"), {
      val: props.shortTitle
    });
    this.logo = new File.png(ref.child("logo"), {
      val: props.logo,
      optional: true
    });
    this.thumbLogo = new File.png(ref.child("thumb_logo"), {
      val: props.thumbLogo,
      optional: true,
      readonly: true
    });
    this.ageGroup = new Property(ref.child("ageGroup"), {
      val: props.ageGroup
    });
    this.genderGroup = new Property(ref.child("genderGroup"), {
      val: props.genderGroup,
      representer: Representers.defaultValue(
        Representers.valueRepresenter(true),
        League.GenderGroup.mixed
      )
    });
    this.coverage = new Property(ref.child("coverage"), {
      val: props.coverage,
      representer: Representers.defaultValue(
        Representers.propsRepresenter(Coverage),
        new Coverage()
      )
    });
    this.manager = new Relation(ref.child("manager"), {
      val: props.manager,
      value: { class: User, property: "leagueManager/league" }
    });
    this.contacts = new Property(ref.child("contacts"), {
      val: props.contacts,
      optional: true
    });

    // football league
    this.tournaments = new Values(ref.child("tournaments"), {
      elementClass: Tournament
    });
    this.teams = new Relations(ref.child("teams"), {
      element: {
        class: Team,
        property: k => `leagues/${k}`
      }
    });
  }
}
League.Manager = class extends RealtimeObject {
  constructor(ref, options = {}) {
    const props = options.props || {};
    super(ref, options);
    this.league = new Relation(ref.child("league"), {
      val: props.league,
      value: { class: League, property: "manager", ownerLevelsUp: 2 }
    });
  }
};
League.GenderGroup = Object.freeze({
  mixed: 0,
  men: 1,
  women: 2
});
exports.League = League;

class Tournament extends RealtimeObject {
  constructor(ref, options = {}) {
    const props = options.props || {};
    super(ref, options);
    this.title = new Property(ref.child("title"), { val: props.title });
    this.type = new Property(ref.child("type"), { val: props.type });
    this.seasons = new Values(statisticRef(ref.child("seasons")), {
      elementClass: Season,
      viewWritable: true
    }); // with view links
  }

  generateLeague() {
    return this.generateParentObjectWith("leagues", League, 0);
  }
}
exports.Tournament = Tournament;

class Season extends RealtimeObject {
  constructor(ref, options = {}) {
    const props = options.props || {};
    super(ref, options);
    this.openDate = new Property(ref.child("openDate"), {
      val: props.openDate
    });
    this.closeDate = new Property(ref.child("closeDate"), {
      val: props.closeDate
    });
    this.closed = new Property(ref.child("closed"), {
      val: props.closed,
      representer: Representers.defaultValue(
        Representers.valueRepresenter(true),
        false
      )
    });
    this.gameFormat = new Property(ref.child("gameFormat"), {
      val: props.gameFormat,
      representer: Representers.propsRepresenter(Season.GameFormat)
    });
    this.scoreTables = new ScoreTables(ref.child("scoreTables"));
    this.participants = new AssociatedValues(ref.child("participants"), {
      keyObject: { class: Team, path: "teams" },
      valueClass: TeamApplication,
      viewWritable: true
    }); // with view links
    this.stages = new Values(ref.child("stages"), {
      elementClass: TournamentStage,
      viewWritable: true
    }); // with view links
  }
}
Season.GameFormat = class {
  constructor(props = {}) {
    this.halfDuration = props.halfDuration;
    this.substitutions = props.substitutions;
    this.teamSize = props.teamSize;
  }
  get nominalGameDuration() {
    return this.halfDuration * 2;
  }
};
exports.Season = Season;

class ScoreTables extends RealtimeObject {
  constructor(ref, options) {
    super(ref, options);
    const conf = {
      keyObject: { class: User, path: "users" },
      valueClass: Number
    };
    this.bombardiers = new AssociatedValues(ref.child("bombardiers"), conf);
    this.assistants = new AssociatedValues(ref.child("assistants"), conf);
    this.goalkeepers = new AssociatedValues(ref.child("goalkeepers"), conf);
    this.yellowCards = new AssociatedValues(ref.child("yellowCards"), conf);
    this.redCards = new AssociatedValues(ref.child("redCards"), conf);
  }
}

class TeamApplication extends RealtimeObject {
  constructor(ref, options = {}) {
    super(ref, options);
    this.roster = new AssociatedValues(ref.child("roster"), {
      keyObject: { class: User, path: "users" },
      valueClass: PlayerInSeason
    });
  }
}
exports.TeamApplication = TeamApplication;

class PlayerInSeason extends RealtimeObject {
  constructor(ref, options) {
    const props = options.props || {};
    super(ref, options);
    this.disqualified = props.disqualified || 0;
  }
}
exports.PlayerInSeason = PlayerInSeason;

class FootballTableRow {
  constructor(props = {}) {
    this.games = props.games;
    this.wins = props.wins;
    this.draws = props.draws;
    this.losses = props.losses;
    this.goals = props.goals;
    this.concededGoals = props.concededGoals;
  }
  get differenceGoals() {
    return this.goals - this.concededGoals;
  }
  get points() {
    return (this.wins || 0) * 3 + (this.draws || 0);
  }
}
FootballTableRow.fromSnapshot = function(snapshot) {
  return new FootballTableRow(snapshot.val());
};
exports.FootballTableRow = FootballTableRow;

class TournamentStage extends RealtimeObject {
  constructor(ref, options = {}) {
    const props = options.props || {};
    super(ref, options);
    this.name = new Property(ref.child("name"), { val: props.name });
    this.gameDays = new Values(ref.child("gameDays"), {
      elementClass: GameDay,
      viewWritable: true,
      ascending: false
    }); // with view links
    this._groups = undefined;
  }

  get isPlayoff() {
    return this.raw == 1;
  }
  get groups() {
    if (!this.isPlayoff && !this._groups) {
      this._groups = new Values(this.ref.child("groups"), {
        elementClass: Group,
        viewWritable: true
      });
    }
    return this._groups;
  }
}
exports.TournamentStage = TournamentStage;

class Group extends RealtimeObject {
  constructor(ref, options = {}) {
    const props = options.props || {};
    super(ref, options);
    this.name = new Property(ref.child("name"), { val: props.name });
    this.table = new AssociatedValues(ref.child("table"), {
      keyObject: { class: Team, path: "teams" },
      valueClass: FootballTableRow
    });
    this.matches = new Relations(ref.child("matches"), {
      element: {
        class: Game,
        path: Utilities.rootPath(Utilities.ancestorOnLevelUp(ref, 2)),
        property: "group"
      }
    });
  }
}
exports.Group = Group;

class GameDay extends RealtimeObject {
  constructor(ref, options = {}) {
    const props = options.props || {};
    super(ref, options);
    this.name = new Property(ref.child("name"), { val: props.name });
    this.closed = new Property(ref.child("closed"), {
      val: props.closed,
      representer: Representers.defaultValue(
        Representers.valueRepresenter(true),
        false
      )
    });
    this.matches = new Values(ref.child("matches"), {
      elementClass: Game,
      viewWritable: true
    }); // with view links
  }

  generateLeague() {
    return this.generateParentObjectWith("leagues", League, 1);
  }

  generateTournament() {
    return this.generateParentObjectWith("tournaments", Tournament, 1);
  }

  generateSeason() {
    return this.generateParentObjectWith("seasons", Season, 0);
  }
}
exports.GameDay = GameDay;

class Game extends RealtimeObject {
  constructor(ref, options = {}) {
    const props = options.props || {};
    super(ref, options);
    // game
    this.location = new Property(ref.child("location"), {
      val: props.location
    });
    this.status = new Property(ref.child("status"), {
      val: props.status,
      representer: Representers.defaultValue(
        Representers.propsRepresenter(Game.Status),
        new Game.Status()
      )
    });
    this.date = new Property(ref.child("date"), { val: props.date });
    // match
    this.group = new Relation(ref.child("group"), {
      val: props.group,
      value: {
        class: Group,
        property: gameKey => `matches/${gameKey}`,
        path: Utilities.rootPath(Utilities.ancestorOnLevelUp(ref, 4))
      },
      optional: true
    });
    this.homeTeam = Reference.Team(ref.child("homeTeam"), {
      val: props.homeTeam
    });
    this.homeTeamRoster = new AssociatedValues(ref.child("homeTeamRoster"), {
      keyObject: { class: User, path: "users" },
      valueClass: PlayerMatchStatistic
    });
    this.homeTeamReserve = new AssociatedValues(ref.child("homeTeamReserve"), {
      keyObject: { class: User, path: "users" },
      valueClass: Number
    });
    this.awayTeam = Reference.Team(ref.child("awayTeam"), {
      val: props.awayTeam
    });
    this.awayTeamRoster = new AssociatedValues(ref.child("awayTeamRoster"), {
      keyObject: { class: User, path: "users" },
      valueClass: PlayerMatchStatistic
    });
    this.awayTeamReserve = new AssociatedValues(ref.child("awayTeamReserve"), {
      keyObject: { class: User, path: "users" },
      valueClass: Number
    });
    this.referees = new Property(ref.child("referees"), {
      val: props.referees,
      optional: true
    });

    this.timeline = new Values(ref.child("timeline"), {
      elementClass: LiveMessage,
      ascending: false
    });
  }

  generateLeague() {
    return this.generateParentObjectWith("leagues", League, 1);
  }

  generateTournament() {
    return this.generateParentObjectWith("tournaments", Tournament, 1);
  }

  generateSeason() {
    return this.generateParentObjectWith("seasons", Season, 0);
  }

  generateStage(raw) {
    const ref = Utilities.refThatHasParentWith("stages", this.ref, 0);
    if (!ref) return null;
    return new TournamentStage(ref, { raw: raw });
  }

  generateGameDay() {
    return this.generateParentObjectWith("gameDays", GameDay, 0);
  }
}
Game.Status = class {
  constructor(props = {}) {
    this.__raw = props.raw || props.__raw || 0;
    this.result = props.result;
  }

  get raw() {
    return this.__raw;
  }
  resultString(divider = ":", placeholder = "_") {
    switch (this.__raw) {
      case 0:
        return `${placeholder} ${divider} ${placeholder}`;
      case 1:
      case 2:
      case 3:
        return `${this.result.homeTeamGoals || 0} ${divider} ${this.result
          .awayTeamGoals || 0}`;
    }
  }

  static scheduled() {
    return new Game.Status();
  }
  static progressing(result) {
    return new Game.Status({ raw: 1, result: result });
  }
  static ended(result) {
    return new Game.Status({ raw: 2, result: result });
  }
  static annulled(result) {
    return new Game.Status({ raw: 3, result: result });
  }
};
exports.Game = Game;

class LiveMessage extends RealtimeObject {
  constructor(ref, options = {}) {
    const props = options.props || {};
    super(ref, options);
    this.time = props.time;
    this.team = Reference.Team(
      ref.child("team"),
      {
        val: props.team,
        optional: this.raw < 1 || this.raw > 4
      },
      true
    );
    this.primaryPerson = Reference.User(
      ref.child("primaryPerson"),
      {
        val: props.primaryPerson,
        optional: this.raw < 1 || this.raw > 4
      },
      true
    );
    this.secondaryPerson = Reference.User(
      ref.child("secondaryPerson"),
      {
        val: props.secondaryPerson,
        optional: this.raw != 2
      },
      true
    );
    this.commentary = props.commentary;
    this.direct = props.direct;
  }

  get minute() {
    return this.time / 60;
  }

  generateStatistic() {
    let statistic = [];
    switch (this.raw) {
      case LiveMessage.Cases.goal:
        statistic.push([this.primaryPerson.value, { goals: 1 }]);
        if (this.secondaryPerson) {
          statistic.push([this.secondaryPerson.value, { assists: 1 }]);
        }
        break;
      case LiveMessage.Cases.substitution:
        statistic.push([
          this.primaryPerson.value,
          {
            playTimeInterval: new PlayerMatchStatistic.PlayTimeInterval({
              end: this.time
            })
          }
        ]);
        statistic.push([
          this.secondaryPerson.value,
          {
            playTimeInterval: new PlayerMatchStatistic.PlayTimeInterval({
              begin: this.time
            })
          }
        ]);
        break;
      case LiveMessage.Cases.yellowCard:
        statistic.push([this.primaryPerson.value, { yellowCards: 1 }]);
        break;
      case LiveMessage.Cases.redCard:
        statistic.push([
          this.primaryPerson.value,
          {
            redCard: true,
            playTimeInterval: new PlayerMatchStatistic.PlayTimeInterval({
              end: this.time
            })
          }
        ]);
        break;
      default:
        break;
    }
    return statistic;
  }
}
LiveMessage.Cases = Object.freeze({
  goal: 1,
  substitution: 2,
  yellowCard: 3,
  redCard: 4,
  commentary: 5
});
exports.LiveMessage = LiveMessage;

function _i(val) {
  return val || 0;
}
function _u(val) {
  return val || undefined;
}
class PlayerMatchStatistic extends RealtimeObject {
  constructor(ref, options = {}) {
    const props = options.props || {};
    super(ref, options);
    this.playTimeInterval =
      props.playTimeInterval || new PlayerMatchStatistic.PlayTimeInterval();
    this.position = props.position || FootballPlayer.Position.universal;
    this.goals = props.goals;
    this.assists = props.assists;
    this.yellowCards = props.yellowCards;
    this.redCard = props.redCard;
    this.concededGoals = props.concededGoals; /// only for goalkeepers
  }

  applyDataForExplicitProperty(key, parentSnapshot) {
    if (key === "playTimeInterval") {
      this.playTimeInterval = new PlayerMatchStatistic.PlayTimeInterval(
        parentSnapshot.child(key).val()
      );
    } else {
      super.applyDataForExplicitProperty(key, parentSnapshot);
    }
  }

  isInGame(time) {
    return (
      this.playTimeInterval.end == undefined || this.playTimeInterval.end > time
    );
  }

  merged(other, event) {
    let merged = this;
    merged.goals = _u(_i(merged.goals) + _i(other.goals));
    merged.assists = _u(_i(merged.assists) + _i(other.assists));
    merged.concededGoals = _u(
      _i(merged.concededGoals) + _i(other.concededGoals)
    );
    if (other.playTimeInterval) {
      merged.playTimeInterval = merged.playTimeInterval.merged(
        other.playTimeInterval
      );
    }
    merged.yellowCards = _u(
      Math.min(2, _i(merged.yellowCards) + _i(other.yellowCards))
    );
    if (merged.yellowCards == 2 && other.yellowCards) {
      /// direct red card is resolved already in playTimeInterval merging
      merged.playTimeInterval = merged.playTimeInterval
        ? merged.playTimeInterval.merged({ end: event.time })
        : new PlayerMatchStatistic.PlayTimeInterval({ end: event.time });
    }
    merged.redCard = _u(other.redCard || merged.yellowCards == 2);
    return merged;
  }

  reverted(other) {
    let reverted = this;
    reverted.goals = _u(Math.max(0, _i(reverted.goals) - _i(other.goals)));
    reverted.assists = _u(
      Math.max(0, _i(reverted.assists) - _i(other.assists))
    );
    reverted.concededGoals = _u(
      Math.max(0, _i(reverted.concededGoals) - _i(other.concededGoals))
    );
    if (other.playTimeInterval) {
      reverted.playTimeInterval = reverted.playTimeInterval.reverted(
        other.playTimeInterval
      );
    }
    reverted.yellowCards = _u(
      Math.max(0, _i(reverted.yellowCards) - _i(other.yellowCards))
    );
    const oldRedCard = reverted.redCard;
    reverted.redCard = _u(reverted.yellowCards == 2);
    if (oldRedCard != reverted.redCard) {
      reverted.playTimeInterval.end = undefined;
    }
    return reverted;
  }
}
PlayerMatchStatistic.PlayTimeInterval = class {
  constructor(props = {}) {
    this.begin = props.begin || 0;
    this.end = props.end;
  }
  merged(other) {
    let merged = this;
    merged.begin = _i(merged.begin) + _i(other.begin);
    merged.end = _u(other.end);
    return merged;
  }
  reverted(other) {
    let reverted = this;
    reverted.begin = _i(reverted.begin) - _i(other.begin);
    if (other.end) {
      reverted.end = undefined;
    }
    return reverted;
  }
};
exports.PlayerMatchStatistic = PlayerMatchStatistic;

User.prototype.statistic = function() {
  return new UserStatistic(statisticRef(this.ref));
};
class UserStatistic extends RealtimeObject {
  constructor(ref, options) {
    super(ref, options);
    this.career = new Values(ref.child("career"), {
      elementClass: CareerStage
    });
    this.values = new AssociatedValues(ref.child("values"), {
      keyObject: {
        class: CareerStage,
        path: Utilities.rootPath(this.career.ref)
      },
      valueClass: CareerStageValue
    });
  }
}
exports.UserStatistic = UserStatistic;

class CareerStage extends RealtimeObject {
  constructor(ref, options = {}) {
    const props = options.props || {};
    super(ref, options);
    this.team = Reference.Team(ref.child("team"), { val: props.team });
    this.beginDate = props.beginDate;
    this.endDate = props.endDate;
  }
}
exports.CareerStage = CareerStage;

class CareerStageValue extends RealtimeObject {
  constructor(ref, options = {}) {
    super(ref, options);
    this.leagues = new AssociatedValues(ref.child("leagues"), {
      keyObject: { class: League, path: "leagues" },
      valueClass: CareerStageValue.League
    });
  }
}

CareerStageValue.League = class extends RealtimeObject {
  constructor(ref, options = {}) {
    super(ref, options);
    this.tournaments = new AssociatedValues(ref.child("tournaments"), {
      keyObject: {
        class: Tournament,
        path: `leagues/${ref.key}/tournaments`
      },
      valueClass: CareerStageValue.League.Tournament
    });
  }
};

CareerStageValue.League.Tournament = class extends RealtimeObject {
  constructor(ref, options = {}) {
    super(ref, options);
    this.seasons = new AssociatedValues(ref.child("seasons"), {
      keyObject: {
        class: Season,
        path:
          "statistic/" +
          Utilities.rootPath(ref)
            .split("/")
            .slice(5)
            .join("/") +
          "/seasons"
      },
      valueClass: CareerStageValue.League.Tournament.Season
    });
  }
};

CareerStageValue.League.Tournament.Season = class extends RealtimeObject {
  constructor(ref, options = {}) {
    const props = options.props || {};
    super(ref, options);
    this.matches = props.matches;
    this.goals = props.goals;
    this.assists = props.assists;
    this.yellowCards = props.yellowCards;
    this.redCards = props.redCards;
    this.avgScore = props.avgScore;
    this.subjectiveScore = props.subjectiveScore;
    this.trophies = props.trophies;
    this.achievements = props.achievements;
  }
};
exports.CareerStageValue = CareerStageValue;

class PageNews extends RealtimeObject {
  constructor(ref, options = {}) {
    const props = options.props || {};
    super(ref, options);
    this.title = props.title;
    this.date = props.date;
    this.subtitle = props.subtitle;
    this.body = props.body;
    this.image = new File.jpeg(ref.child("image"), {
      val: props.image,
      optional: true
    });
    this.thumbImage = new File.jpeg(ref.child("thumb_image"), {
      val: props.thumbImage,
      readonly: true,
      optional: true
    });
  }

  generatePublisher() {
    let publisher = this.generateParentObjectWith("leagues", League, 1);
    if (publisher) {
      publisher = this.generateParentObjectWith("teams", Team, 1);
    }
    return publisher;
  }
}
exports.PageNews = PageNews;

class MessageSubject extends Property {
  encode(value) {
    if (value instanceof User) {
      return { raw: 1, ref: value.key };
    } else if (value instanceof Team) {
      return { raw: 2, ref: value.key };
    } else if (value instanceof League) {
      return { raw: 3, ref: value.key };
    } else {
      if (process.env.NODE_ENV !== "production") {
        console.error("Unexpected subject value", value);
      }
      throw Error("Unexpected subject value");
    }
  }
  apply(snapshot) {
    if (snapshot.exists()) {
      const val = snapshot.val();
      if (!val.raw || !val.ref) throw Error("unexpected-snapshot-value");
      let value;
      switch (val.raw) {
        case 1:
          value = new User(snapshot.ref.root.child(`users/${val.ref}`));
          break;
        case 2:
          value = new Team(snapshot.ref.root.child(`teams/${val.ref}`));
          break;
        case 3:
          value = new League(snapshot.ref.root.child(`leagues/${val.ref}`));
          break;
        default:
          throw Error("unexpected-snapshot-value");
      }
      this.value = value;
    } else {
      this.value = null;
      return null;
    }
  }
}

class MessageAction {
  constructor(raw, subjectRef) {
    this.raw = raw;
    this.ref = subjectRef;
  }

  static teamInvitation(team) {
    // team sent invitation to current user
    return new MessageAction(1, team.key);
  }
  static leagueInvitation(league) {
    // league sent invitation to current user`s team
    return new MessageAction(2, league.key);
  }
  static teamParticipation(user) {
    // some user sent request to current user`s team
    return new MessageAction(3, user.key);
  }
  static leagueParticipation(team) {
    // some team sent request to current user`s league
    return new MessageAction(4, team.key);
  }
}
exports.MessageAction = MessageAction;

class ServiceMessageEvent {
  constructor(raw, res) {
    this.raw = raw;
    this.res = res;
  }

  static additionToTeam(result) {
    return new ServiceMessageEvent(1, result);
  }
  static additionToLeague(result) {
    return new ServiceMessageEvent(2, result);
  }
}
exports.ServiceMessageEvent = ServiceMessageEvent;

class Message extends RealtimeObject {
  constructor(ref, options = {}) {
    const props = options.props || {};
    super(ref, options);
    this.date = props.date;
    this.target = new MessageSubject(ref.child("target"), {
      val: props.target
    });
    this.source = new MessageSubject(ref.child("source"), {
      val: props.source
    });
    this.message = props.message;

    // service only
    this.serviceEvent = props.serviceEvent;
    this.body = props.body;
    this.image = props.image;

    // request only
    this.action = props.action;

    // response only
    this.requestMessage = props.requestMessage;
    this.accepted = props.accepted;
  }

  write(transaction) {
    ["raw", "date", "target", "source", "message"].forEach(key => {
      if (this[key] == null) {
        console.error("Required property does not set with key", key);
        throw Error("Some required property does not set");
      }
    });
    super.write(transaction);
  }

  get sourceThumbImage() {
    const val = this.source.value;
    if (val instanceof User) {
      return val.thumbPhoto;
    } else if (val instanceof Team || val instanceof League) {
      return val.thumbLogo;
    } else {
      throw Error("unexpected-behavior");
      return null;
    }
  }

  get sourceName() {
    const val = this.source.value;
    if (val instanceof User || val instanceof Team) {
      return val.name;
    } else if (val instanceof League) {
      return val.title;
    } else {
      throw Error("unexpected-behavior");
      return null;
    }
  }

  title(translator) {
    switch (this.__raw) {
      case 1:
        return this.actionTitle(translator);
      case 2:
        return translator.$t("RequestWas", [translator.$t(this.accepted ? "Confirmed" : "Rejected").toLowerCase()]);
      default:
        if (!this.serviceEvent || !this.serviceEvent.raw) {
          return translator.$t("Notification");
        }
        switch (this.serviceEvent.raw) {
          case 1:
            return translator.$t(this.serviceEvent.res
              ? "YouAreInTeam"
              : "InvitationWasAnnulled");
          case 2:
            return translator.$t(this.serviceEvent.res
              ? "YouAreInLeague"
              : "InvitationWasAnnulled");
          default:
            return null;
        }
    }
  }

  actionTitle(translator) {
    if (!this.action || !this.action.raw) return null;
    switch (this.action.raw) {
      case 1:
        return translator.$t("InvitationFrom", [translator.$t("Team_0_1").toLowerCase()]);
      case 2:
        return translator.$t("InvitationFrom", [translator.$t("League_0_1").toLowerCase()]);
      case 3:
        return translator.$t("RequestFrom", [translator.$t("User_0_1").toLowerCase()]);
      case 4:
        return translator.$t("RequestFrom", [translator.$t("Team_0_1").toLowerCase()]);
      default:
        return "";
    }
  }

  responseActionTitle(translator) {
    if (!this.action || !this.action.raw) return null;
    switch (this.action.raw) {
      case 1:
        return translator.$t('ResponseForAction', [translator.$t("InvitationTo", [translator.$t("Team_0_6")]).toLowerCase()]);
      case 2:
        return translator.$t('ResponseForAction', [translator.$t("InvitationTo", [translator.$t("League_0_6")]).toLowerCase()]);
      case 3:
        return translator.$t('ResponseForAction', [translator.$t("RequestTo", [translator.$t("Team_0_6")]).toLowerCase()]);
      case 4:
        return translator.$t('ResponseForAction', [translator.$t("RequestTo", [translator.$t("League_0_6")]).toLowerCase()]);
      default:
        return "";
    }
  }
}
Message.Status = Object.freeze({
  empty: 0,
  isRead: 1 << 0,
  isAccepted: 1 << 1,
  isDeclined: 1 << 2,
  isConfirmed: 1 << 3,
  isRespondedSpace() {
    return this.isAccepted | this.isDeclined | this.isConfirmed;
  }
});
Message.request = function(ref, props) {
  return new Message(ref, { raw: 1, props: props });
};
Message.response = function(ref, props) {
  return new Message(ref, { raw: 2, props: props });
};
Message.service = function(ref, props) {
  return new Message(ref, { raw: 0, props: props });
};
exports.Message = Message;

/// Subscription has different implementation with iOS version:
/// It uses explicit feed reference, because Relations has insufficiently reference managment options.
class Subscription extends RealtimeValue {}
Subscription.teamNews = function(team) {
  if (!(team instanceof Team)) {
    throw Error("Unexpected type");
  }
  return new Subscription(feedRef(team.ref), { raw: 1 });
};
Subscription.leagueNews = function(league) {
  if (!(league instanceof League)) {
    throw Error("Unexpected type");
  }
  return new Subscription(feedRef(league.ref), { raw: 2 });
};
exports.Subscription = Subscription;
class Subscriber extends RealtimeValue {}
Subscriber.user = function(user) {
  if (!(user instanceof User)) {
    throw Error("Unexpected type");
  }
  return new Subscriber(feedRef(user.ref));
};
exports.Subscriber = Subscriber;